import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import { ReactGridContainer } from '@europrocurement/flexy-components';
import { type ResizeHandleAxis } from 'react-grid-layout';
import { faFolder } from '@fortawesome/pro-regular-svg-icons';
import { useSnackbar } from 'notistack';
import { DOSSIERS_OPEN_SEARCH_SLICE_NAME, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    VALIDATOR_TYPE_GREFFE,
    VALIDATOR_TYPE_GREFFE_INPI,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { DossierOpenSearchJsonldOpensearchDossierValideursStatusRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DashboardStatCardTotalBar from '../molecules/DashboardStatCardTotalBar/DashboardStatCardTotalBar';
import WidgetWithAction from '../molecules/WidgetWithAction/WidgetWithAction';
import { type DashboardFiltersType } from '../../containers/FormalistDashboard/FormalistDashboard';

type GreffesStatusWidgetDashboardProps = {
    localStorageName: string;
    formalistIds: number[];
    filters: DashboardFiltersType;
    listeDossiersPath: string;
    formalistName?: string;
};
// Récupérer les valideurs de type greffe liés aux dossiers
const GreffesStatusWidgetDashboard: React.FunctionComponent<GreffesStatusWidgetDashboardProps> =
    function ({ formalistIds, filters, listeDossiersPath, localStorageName, formalistName }) {
        const { palette } = useTheme();
        const { getValidatorStatusStyle } = useStatusStyle();
        const { enqueueSnackbar } = useSnackbar();
        const { dossierOpenSearchApi } = useFormaliteService();
        const dispatch = useDispatch();
        const navigate = useNavigate();

        const handleGreffeStatusClick = (statusId?: number | null) => {
            if (!statusId) return;

            dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'formalisteId',
                    value: formalistIds,
                },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'dateCreation', value: filters },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'statutValideurId',
                    value: [statusId],
                },
            });

            if (formalistName) {
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                    payload: { key: 'formalisteId', label: formalistName },
                });
            }

            navigate(`/${listeDossiersPath}`);
        };

        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const [hasFetched, setHasFetched] = useState<boolean>(false);
        const [statusGreffes, setStatusGreffes] = useState<
            DossierOpenSearchJsonldOpensearchDossierValideursStatusRead[]
        >([]);

        const maxXindexCard = 5;
        const currentXIndexCard = useRef(2); // Initial position at the 3 column
        const currentYIndexCard = useRef(0);

        const fetchGreffesStatus = async () => {
            if (isSubmitting && !formalistIds) return;

            setIsSubmitting(true);
            const dossierStatusResponse =
                await dossierOpenSearchApi.apiOpensearchValideursStatusGetCollection({
                    formalisteId: formalistIds,
                    itemsPerPage: 20,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                });

            setIsSubmitting(false);
            setHasFetched(true);
            if (dossierStatusResponse.status === 200) {
                setStatusGreffes(
                    dossierStatusResponse.data['hydra:member']
                        .filter(
                            (status) =>
                                [VALIDATOR_TYPE_GREFFE_INPI, VALIDATOR_TYPE_GREFFE].includes(
                                    status.idValideur as number,
                                ) &&
                                status.sortOrder != null &&
                                status.libelleStatus !== 'Validé',
                        )
                        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
                );
            } else {
                enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
                    variant: 'error',
                });
                setStatusGreffes([]);
            }
        };

        useEffect(() => {
            fetchGreffesStatus();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formalistIds]);

        if (!hasFetched) return null;

        return (
            <ReactGridContainer
                localStorageName={localStorageName}
                gridconfig={{ cols: 6, rowHeight: 200 }}
                initialXIndexCard={2}
                currentXIndexCard={currentXIndexCard}
                currentYIndexCard={currentYIndexCard}
                // loop thru status greffes to generate a layout of key and position in react grid
                layoutProps={[
                    {
                        i: `${localStorageName}_recap`,
                        x: 0,
                        y: 0,
                        w: 3,
                        h: 1,
                        isResizable: true,
                        resizeHandles: [
                            's',
                            'w',
                            'e',
                            'n',
                            'sw',
                            'nw',
                            'se',
                            'ne',
                        ] as ResizeHandleAxis[],
                    },
                    ...statusGreffes.map((statusGreffe) => {
                        if (
                            currentXIndexCard.current !== 0 &&
                            currentXIndexCard.current % maxXindexCard === 0
                        ) {
                            // In the first row there is a space for 3 items, in the others row there is space for 6 items
                            currentXIndexCard.current = -1;
                            currentYIndexCard.current++;
                        }
                        currentXIndexCard.current++;
                        return {
                            i: `${localStorageName}_${statusGreffe.libelleValideur}_${statusGreffe.idStatus}`,
                            x: currentXIndexCard.current,
                            y: currentYIndexCard.current,
                            w: 1,
                            h: 1,
                            isResizable: true,
                            resizeHandles: [
                                's',
                                'w',
                                'e',
                                'n',
                                'sw',
                                'nw',
                                'se',
                                'ne',
                            ] as ResizeHandleAxis[],
                        };
                    }),
                ]}
            >
                <Box
                    key={`${localStorageName}_recap`}
                    sx={{
                        width: '100%', // Width and height based on parent size
                        height: '100%',
                        background: `linear-gradient(to bottom, ${palette.grey[200]},  ${palette.grey[50]})`,
                        border: `1px solid ${palette.grey[400]}`,
                        boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
                        borderRadius: '15px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: '4px',
                    }}
                >
                    <DashboardStatCardTotalBar
                        items={statusGreffes.map((statusGreffe) => ({
                            id: statusGreffe.statusFlag,
                            libelle: statusGreffe.libelleStatus,
                            childsCount: statusGreffe.childsCount || 0,
                            key: `${statusGreffe.idValideur}_${statusGreffe.idStatus}`,
                        }))}
                        title="Suivi greffes — flux actif"
                        icon={faFolder}
                        handleItemClick={handleGreffeStatusClick}
                        handleStyleStatus={getValidatorStatusStyle}
                    />
                </Box>

                {statusGreffes.map((statusGreffe) => {
                    const { paletteString, namedIcon, sxStatus } = getValidatorStatusStyle(
                        statusGreffe.statusFlag as number,
                    );
                    return (
                        <Box
                            key={`${localStorageName}_${statusGreffe.libelleValideur}_${statusGreffe.idStatus}`}
                        >
                            <WidgetWithAction
                                title={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                            variant="l2dh6"
                                            fontWeight="bold"
                                        >
                                            {statusGreffe.libelleStatus}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {statusGreffe.libelleValideur}
                                        </Typography>
                                    </Box>
                                }
                                icon={namedIcon}
                                actionMessage="Voir"
                                actionCallback={async () =>
                                    handleGreffeStatusClick(statusGreffe.idStatus as number)
                                }
                                data={statusGreffe.childsCount || '0'}
                                color={paletteString}
                                sx={sxStatus}
                            />
                        </Box>
                    );
                })}
            </ReactGridContainer>
        );
    };

export default GreffesStatusWidgetDashboard;
